import React from 'react';

/**
 * Marketing-only iPhone frame for the redesigned landing page.
 * Renders a 390x848 device shell with optional dynamic island + status bar.
 *
 * `bare` mode hides the rendered status bar + dynamic island so a real
 * iOS screenshot (which already includes a status bar) can fill the screen.
 * The home indicator is always drawn on top.
 *
 * Scoped to /features/marketing/ — do not import inside the app.
 */

type Props = {
  children: React.ReactNode;
  width?: number;
  height?: number;
  dark?: boolean;
  bare?: boolean;
  className?: string;
};

const IOSDevice: React.FC<Props> = ({
  children,
  width = 390,
  height = 848,
  dark = false,
  bare = false,
  className,
}) => {
  return (
    <div
      className={`lp-ios-device${dark ? ' is-dark' : ''}${className ? ` ${className}` : ''}`}
      style={{ width, height }}
    >
      {!bare && (
        <>
          <div className="lp-ios-island" aria-hidden="true" />
          <div className="lp-ios-statusbar" aria-hidden="true">
            <span className="lp-ios-time">9:41</span>
            <span className="lp-ios-icons">
              <svg width="19" height="12" viewBox="0 0 19 12">
                <rect x="0" y="7.5" width="3.2" height="4.5" rx="0.7" />
                <rect x="4.8" y="5" width="3.2" height="7" rx="0.7" />
                <rect x="9.6" y="2.5" width="3.2" height="9.5" rx="0.7" />
                <rect x="14.4" y="0" width="3.2" height="12" rx="0.7" />
              </svg>
              <svg width="17" height="12" viewBox="0 0 17 12">
                <path d="M8.5 3.2C10.8 3.2 12.9 4.1 14.4 5.6L15.5 4.5C13.7 2.7 11.2 1.5 8.5 1.5C5.8 1.5 3.3 2.7 1.5 4.5L2.6 5.6C4.1 4.1 6.2 3.2 8.5 3.2Z" />
                <path d="M8.5 6.8C9.9 6.8 11.1 7.3 12 8.2L13.1 7.1C11.8 5.9 10.2 5.1 8.5 5.1C6.8 5.1 5.2 5.9 3.9 7.1L5 8.2C5.9 7.3 7.1 6.8 8.5 6.8Z" />
                <circle cx="8.5" cy="10.5" r="1.5" />
              </svg>
              <svg width="27" height="13" viewBox="0 0 27 13">
                <rect x="0.5" y="0.5" width="23" height="12" rx="3.5" stroke="currentColor" strokeOpacity="0.35" fill="none" />
                <rect x="2" y="2" width="20" height="9" rx="2" />
                <path d="M25 4.5V8.5C25.8 8.2 26.5 7.2 26.5 6.5C26.5 5.8 25.8 4.8 25 4.5Z" fillOpacity="0.4" />
              </svg>
            </span>
          </div>
        </>
      )}

      <div className="lp-ios-content">{children}</div>

      <div className="lp-ios-home" aria-hidden="true">
        <div className="lp-ios-home-bar" />
      </div>
    </div>
  );
};

export default IOSDevice;
